import { memo, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  AuraLayout,
  Button,
  Layout,
  SupportIcon,
  Title,
} from "lola-framework-ui-test";
import { useSupportSheet } from "../../components/SupportSheet";
import { Popup } from "../../components/Popup";
import { CvvPrepareGate } from "../../components/CvvPrepareGate";
import { FloatingField } from "../../components/FloatingField";
import { DEFERRED_ORIGINATION_ROUTES, TITLE_COLOR } from "../../constants";
import { useGalleryPickerLoading } from "../../hooks/useGalleryPickerLoading";
import { useGalleryStore } from "../../gallery/galleryStore";
import { useStore } from "../../store/globalState";
import { useDeferredOriginationStore } from "../../store/useDeferredOriginationStore";
import { deferredOriginationRecentRecipients } from "../../gallery/deferredOriginationMock";
import { paymentType } from "../../interfaces/payment";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";
import { Navbar } from "../../components/Navbar";


const toTitleCase = (value: string): string =>
  value
    .toLowerCase()
    .replace(
      /[^\s-]+/gu,
      (word) => word.charAt(0).toUpperCase() + word.slice(1)
    );

/**
 * Recipient identity step (gallery). Confirm completes the draft and goes to
 * Minting (CIP omitted). Optional CVV prepare popup for card funding inspection.
 */
export const RecipientPage = memo(() => {
  const { t } = useTranslation("deferred-origination");
  const navigate = useNavigate();
  const { theme, paymentState } = useStore();
  const {
    composeDraft,
    draft,
    setDraft,
    pendingRecipientName,
    setPendingRecipientName,
  } = useDeferredOriginationStore();

  const savedListsUi = useGalleryStore((s) => s.savedListsUi);
  // Stand-in for the originations GET the live flow derives the list from.
  const recentLoadDelay = useGalleryPickerLoading(savedListsUi !== "empty");
  const recentBusy = savedListsUi === "loading" || recentLoadDelay;

  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, bodyCopy, dropzoneColors } = colorConfig;
  const { openSupport, supportSheet } = useSupportSheet("deferred-origination");
  const fieldColors = {
    background: colorConfig.background,
    title: colorConfig.title,
    bodyCopy,
    inactiveColor: colorConfig.subtitile,
    errorColor: colorConfig.errorColor,
  };

  const seededName =
    pendingRecipientName || draft?.recipientName || "";
  const [recipientName, setRecipientNameState] = useState(() =>
    seededName ? toTitleCase(seededName) : ""
  );
  const [shakeRecipient, setShakeRecipient] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [showCvvGate, setShowCvvGate] = useState(false);

  const setRecipientName = useCallback(
    (name: string) => {
      const formatted = toTitleCase(name);
      setRecipientNameState(formatted);
      setPendingRecipientName(formatted);
    },
    [setPendingRecipientName]
  );

  const trimmedRecipient = recipientName.trim();
  const hasRecipient =
    trimmedRecipient.split(/\s+/).filter(Boolean).length >= 2 &&
    trimmedRecipient.length >= 3;

  const isCardFunding =
    (composeDraft?.fundingMethod?.includes("CARD") ?? false) ||
    paymentState.payment?.type === paymentType.CARD;

  const finishToMinting = useCallback(() => {
    const base = composeDraft;
    if (!base) {
      navigate(DEFERRED_ORIGINATION_ROUTES.TRANSFER, { replace: true });
      return;
    }
    setDraft({
      ...base,
      recipientName: trimmedRecipient,
    });
    navigate(DEFERRED_ORIGINATION_ROUTES.MINTING, { replace: true });
  }, [composeDraft, navigate, setDraft, trimmedRecipient]);

  const handleConfirm = useCallback(() => {
    if (!hasRecipient) {
      setShakeRecipient(true);
      setTimeout(() => setShakeRecipient(false), 500);
      return;
    }
    if (!composeDraft) {
      navigate(DEFERRED_ORIGINATION_ROUTES.TRANSFER, { replace: true });
      return;
    }

    // Gallery: simulate CVS prepare needing CVV for card funding.
    if (isCardFunding && !showCvvGate) {
      setShowCvvGate(true);
      return;
    }

    setPreparing(true);
    window.setTimeout(() => {
      setPreparing(false);
      finishToMinting();
    }, 400);
  }, [
    hasRecipient,
    composeDraft,
    navigate,
    isCardFunding,
    showCvvGate,
    finishToMinting,
  ]);

  const brandFromPayment = paymentState.payment?.brand ?? "VISA-4242";
  const last4Match = brandFromPayment.match(/(\d{4})$/);
  const last4 = last4Match?.[1] ?? "4242";
  const brand = brandFromPayment.split("-")[0];

  return (
    <>
      <AuraLayout
        colorConfig={colorConfig}
        className="scrollable need-help-header"
      >
        <Layout.Header>
          <Navbar
            title={t("common.needHelp")}
            align="right"
            onBackClick={() => navigate(DEFERRED_ORIGINATION_ROUTES.TRANSFER)}
            onIconClick={openSupport}
            icon={<SupportIcon size={35} colors={dropzoneColors} />}
          />
        </Layout.Header>
        <Layout.Content className="recipient-section">
          <ScrollFadeIndicator color={bodyCopy} />
          <Title title={t("recipient.title")} align="left" color={TITLE_COLOR} />
          <p className="recipient-name-hint">{t("recipient.description")}</p>

          <section
            className={`recipient-name-field ${shakeRecipient ? "shake" : ""}`}
          >
            {recentBusy && (
              <section
                className="recipient-recent"
                aria-busy="true"
                aria-label={t("recipient.loadingRecent")}
              >
                <span className="recipient-recent-label">
                  {t("recipient.recentLabel")}
                </span>
                <div className="recipient-recent-list" aria-hidden="true">
                  {Array.from({ length: 2 }, (_, i) => (
                    <div key={i} className="recipient-recent-skeleton">
                      <span className="recipient-recent-skeleton-line" />
                      <span className="recipient-recent-skeleton-line short" />
                    </div>
                  ))}
                </div>
              </section>
            )}

            {!recentBusy && savedListsUi !== "empty" && (
              <section
                className="recipient-recent"
                aria-label={t("recipient.recentAria")}
              >
                <span className="recipient-recent-label">
                  {t("recipient.recentLabel")}
                </span>
                <div role="list" className="recipient-recent-list">
                  {deferredOriginationRecentRecipients.map((entry) => {
                    const selected =
                      trimmedRecipient.toLowerCase() ===
                      entry.fullName.toLowerCase();
                    return (
                      <button
                        key={entry.fullName}
                        type="button"
                        role="listitem"
                        className={`recipient-recent-item${selected ? " selected" : ""}`}
                        onClick={() => setRecipientName(entry.fullName)}
                      >
                        <span className="recipient-recent-name">
                          {entry.fullName}
                        </span>
                        <span
                          className="recipient-recent-meta"
                          style={{ color: bodyCopy }}
                        >
                          {t("recipient.timesUsed", {
                            count: entry.timesUsed,
                          })}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <span className="recipient-or-new">
                  {t("recipient.orEnterNew")}
                </span>
              </section>
            )}

            <div className="recipient-input-label">
              <FloatingField
                label={t("recipient.fullNameLabel")}
                value={recipientName}
                colors={fieldColors}
              >
                <input
                  type="text"
                  style={{ color: bodyCopy }}
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  autoComplete="name"
                  autoCapitalize="words"
                />
              </FloatingField>
            </div>
            <span className="recipient-name-field-hint">
              {t("recipient.fullNameHint")}
            </span>
          </section>
        </Layout.Content>
        <Layout.Footer>
          <section className="footer-section">
            <div
              className={`button-wrapper ${
                hasRecipient || preparing ? "rainbow-ready" : ""
              }`}
            >
              <Button
                size="large"
                background={backgroundBtn}
                color={textColorBtn}
                disabled={!hasRecipient || preparing}
                onClick={handleConfirm}
              >
                {preparing
                  ? t("transfer.prepare.loading")
                  : t("common.continue")}
              </Button>
            </div>
          </section>
        </Layout.Footer>
      </AuraLayout>

      {showCvvGate ? (
        <Popup
          visible
          onClose={() => setShowCvvGate(false)}
        >
          <CvvPrepareGate
            ns="deferred-origination"
            last4={last4}
            brand={brand}
            loading={preparing}
            onClose={() => setShowCvvGate(false)}
            onSubmit={() => {
              setPreparing(true);
              window.setTimeout(() => {
                setPreparing(false);
                setShowCvvGate(false);
                finishToMinting();
              }, 400);
            }}
          />
        </Popup>
      ) : null}
      {supportSheet}
    </>
  );
});
